import React, { useState, useEffect, useRef } from 'react';

export interface ListOption {
  readonly listId: string;
  readonly title: string;
}

export interface KeyboardCardMoveProps {
  readonly isActive: boolean;
  readonly cardTitle: string;
  readonly currentListId: string;
  readonly lists: ListOption[];
  readonly onMove: (targetListId: string) => Promise<{ success: boolean }>;
  readonly onCancel: () => void;
}

/**
 * Keyboard-based card movement component.
 * User selects target list with Arrow keys, confirms with Enter, cancels with Escape.
 * Includes screen reader announcements via ARIA live region.
 */
export function KeyboardCardMove({
  isActive,
  cardTitle,
  currentListId,
  lists,
  onMove,
  onCancel,
}: KeyboardCardMoveProps): React.ReactElement | null {
  const currentIndex = lists.findIndex((l) => l.listId === currentListId);
  const [selectedIndex, setSelectedIndex] = useState(currentIndex >= 0 ? currentIndex : 0);
  const [announcement, setAnnouncement] = useState('');
  const regionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) {
      setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);
      setAnnouncement(`Moving card "${cardTitle}". Use arrow keys to select target list, Enter to confirm, Escape to cancel.`);
      regionRef.current?.focus();
    }
  }, [isActive, currentIndex, cardTitle]);

  if (!isActive) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (selectedIndex + 1) % lists.length;
        setSelectedIndex(next);
        setAnnouncement(`${lists[next]!.title}${lists[next]!.listId === currentListId ? ' (current)' : ''}`);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = (selectedIndex - 1 + lists.length) % lists.length;
        setSelectedIndex(prev);
        setAnnouncement(`${lists[prev]!.title}${lists[prev]!.listId === currentListId ? ' (current)' : ''}`);
        break;
      }
      case 'Enter': {
        e.preventDefault();
        const targetList = lists[selectedIndex]!;
        onMove(targetList.listId).then((result) => {
          if (result.success) {
            setAnnouncement(`Card "${cardTitle}" moved to "${targetList.title}"`);
          } else {
            setAnnouncement(`Move failed. Card returned to original position.`);
          }
        });
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setAnnouncement(`Move cancelled. Card remains in current list.`);
        onCancel();
        break;
      }
    }
  };

  return (
    <div
      ref={regionRef}
      role="region"
      aria-label={`Move card: ${cardTitle}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="absolute z-40 bg-white border border-gray-300 rounded-lg shadow-lg p-3 w-64"
    >
      <p className="text-sm font-medium mb-2 text-gray-700">Move to list:</p>
      <div role="listbox" aria-activedescendant={`option-${selectedIndex}`}>
        {lists.map((list, idx) => {
          const isCurrent = list.listId === currentListId;
          const isSelected = idx === selectedIndex;
          return (
            <div
              key={list.listId}
              id={`option-${idx}`}
              role="option"
              aria-selected={isSelected}
              aria-label={isCurrent ? `${list.title} (current)` : list.title}
              className={`px-3 py-2 rounded text-sm cursor-default ${
                isSelected ? 'bg-primary-100 font-medium' : ''
              } ${isCurrent ? 'text-gray-500' : ''}`}
            >
              {list.title}{isCurrent ? ' (current)' : ''}
            </div>
          );
        })}
      </div>
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
